import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthFlowModal } from '@/components/blocks/AuthentificationFlow/parts/AuthFlowModal';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { AuthFlowDrawer } from '@/components/blocks/AuthentificationFlow/parts/AuthFlowDrawer';
import { RegistrationOrigin } from '@/data/Content/Registration';
import { useLockBodyScroll } from '@/utils/useLockBodyScroll';
import { LoginOriginProvider } from '@/data/context/loginOrigin';
import { LoginOrigin } from '@/data/types/GTM';
import { EventsContext } from '@/data/context/events';
import { useSettings } from '@/data/Settings';

export enum AuthFlowAction {
	LOGIN = 'login',
	REGISTER = 'register',
}

type AuthentificationDrawerProps = {
	open: boolean;
	onClose: () => void;
	type?: 'login' | 'wishlist' | 'dp-fan';
	origin?: RegistrationOrigin;
	onAuthSuccess?: () => void;
};

const resolveLoginOrigin = (type: AuthentificationDrawerProps['type']): LoginOrigin => {
	if (type === 'wishlist' || type === 'dp-fan') return 'wishlist_modal';
	return 'cart_modal';
};

export const AuthentificationFlow: React.FC<AuthentificationDrawerProps> = ({
	open,
	type,
	origin,
	onAuthSuccess,
	onClose,
}) => {
	const [action, setAction] = useState<AuthFlowAction>(AuthFlowAction.LOGIN);
	const isMobil = useIsMobile();
	useLockBodyScroll(open);
	const login_origin = resolveLoginOrigin(type);
	const { onLoginOpen, onRegisterBegin } = useContext(EventsContext);
	const { settings } = useSettings();

	useEffect(() => {
		if (open) {
			onLoginOpen({ gtm: { flow: 'login', settings, login_origin } });
		}
	}, [open, onLoginOpen, settings, login_origin]);

	const handleOnGo = useCallback(
		(next: AuthFlowAction) => {
			if (next === AuthFlowAction.REGISTER && action !== AuthFlowAction.REGISTER) {
				onRegisterBegin({ gtm: { settings, login_origin } });
			}
			setAction(next);
		},
		[action, onRegisterBegin, settings, login_origin]
	);

	const handleOnClose = () => {
		setAction(AuthFlowAction.LOGIN);
		onClose();
	};

	if (!open) return null;

	return (
		<LoginOriginProvider origin={login_origin}>
			{isMobil ? (
				<AuthFlowDrawer
					action={action}
					open={open}
					origin={origin}
					onClose={handleOnClose}
					onGo={handleOnGo}
					onAuthSuccess={onAuthSuccess}
				/>
			) : (
				<AuthFlowModal
					action={action}
					type={type}
					origin={origin}
					onAuthSuccess={onAuthSuccess}
					onClose={handleOnClose}
					onGo={handleOnGo}
				/>
			)}
		</LoginOriginProvider>
	);
};
